import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, TwoFaProvider } from '../contexts/AuthContext';
import Image from 'next/image';

type LoginStep = 'credentials' | 'twofa';

export function LoginForm() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { login, verify2FA, send2FACode } = useAuth();

  // 2FA state
  const [step, setStep] = useState<LoginStep>('credentials');
  const [verificationCode, setVerificationCode] = useState('');
  const [preVerificationToken, setPreVerificationToken] = useState('');
  const [providers, setProviders] = useState<TwoFaProvider[]>([]);
  const [selectedProvider, setSelectedProvider] = useState('');

  const needsCodeSend = selectedProvider === 'SMS' || selectedProvider === 'EMAIL';

  const sendCode = useCallback(async (token: string, providerType: string) => {
    await send2FACode(token, providerType);
  }, [send2FACode]);

  // When entering 2FA step with SMS/EMAIL, auto-send the code
  useEffect(() => {
    if (step === 'twofa' && needsCodeSend && preVerificationToken) {
      sendCode(preVerificationToken, selectedProvider);
    }
  }, [step, needsCodeSend, preVerificationToken, selectedProvider, sendCode]);

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    const result = await login(email, password);

    if ('requires2FA' in result) {
      setPreVerificationToken(result.preVerificationToken);
      setProviders(result.providers);
      const defaultProvider = result.providers.find(p => p.default) || result.providers[0];
      setSelectedProvider(defaultProvider?.type || '');
      setStep('twofa');
    } else if ('success' in result && !result.success) {
      setError(result.error);
    }
    // success: true — AuthContext sets user, LoginForm unmounts via _app.tsx

    setIsLoading(false);
  };

  const handleVerifySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    const success = await verify2FA(preVerificationToken, selectedProvider, verificationCode);
    if (!success) {
      setError('Invalid verification code. Please try again.');
    }

    setIsLoading(false);
  };

  const handleBackToLogin = () => {
    setStep('credentials');
    setVerificationCode('');
    setPreVerificationToken('');
    setProviders([]);
    setSelectedProvider('');
    setError('');
  };

  const handleResendCode = async () => {
    setError('');
    const success = await send2FACode(preVerificationToken, selectedProvider);
    if (!success) {
      setError('Failed to resend code. Please try again.');
    }
  };

  const getProviderLabel = (providerType: string): string => {
    switch (providerType) {
      case 'TOTP': return 'Enter the code from your authenticator app';
      case 'SMS': return 'A verification code has been sent to your phone';
      case 'EMAIL': return 'A verification code has been sent to your email';
      default: return 'Enter your verification code';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-struvital-light to-struvital-dark flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          {/* Struvital Logo */}
          <div className="flex justify-center mb-6">
            <Image
              src="/struvital-logo.png"
              alt="Struvital"
              width={200}
              height={80}
              priority
              className="h-20 w-auto"
            />
          </div>
          <h2 className="text-3xl font-bold text-white">
            Monitoring Dashboard
          </h2>
          <p className="mt-2 text-struvital-light">
            {step === 'credentials'
              ? 'Sign in with your ThingsBoard credentials'
              : 'Two-factor authentication'}
          </p>
        </div>

        <div className="bg-white/10 backdrop-blur-sm rounded-lg shadow-xl p-8 border border-white/20">
          {error && (
            <div className="bg-red-500/20 border border-red-500/50 text-white px-4 py-3 rounded-md mb-6">
              {error}
            </div>
          )}

          {step === 'credentials' ? (
            <form className="space-y-6" onSubmit={handleLoginSubmit}>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-white">
                  Email address
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="appearance-none relative block w-full px-3 py-2 border border-white/30 placeholder-gray-400 text-gray-900 rounded-md focus:outline-none focus:ring-struvital-primary focus:border-struvital-primary focus:z-10 sm:text-sm bg-white/90"
                    placeholder="Enter your email"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-white">
                  Password
                </label>
                <div className="mt-1">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="appearance-none relative block w-full px-3 py-2 border border-white/30 placeholder-gray-400 text-gray-900 rounded-md focus:outline-none focus:ring-struvital-primary focus:border-struvital-primary focus:z-10 sm:text-sm bg-white/90"
                    placeholder="Enter your password"
                  />
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-struvital-primary hover:bg-struvital-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-struvital-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Signing in...
                    </>
                  ) : (
                    'Sign in'
                  )}
                </button>
              </div>
            </form>
          ) : (
            <form className="space-y-6" onSubmit={handleVerifySubmit}>
              <p className="text-sm text-white/90" data-testid="2fa-provider-label">
                {getProviderLabel(selectedProvider)}
              </p>

              {providers.length > 1 && (
                <div>
                  <label htmlFor="provider" className="block text-sm font-medium text-white">
                    Verification method
                  </label>
                  <select
                    id="provider"
                    value={selectedProvider}
                    onChange={(e) => setSelectedProvider(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-white/30 text-gray-900 rounded-md focus:outline-none focus:ring-struvital-primary focus:border-struvital-primary sm:text-sm bg-white/90"
                  >
                    {providers.map((p) => (
                      <option key={p.type} value={p.type}>{p.type}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label htmlFor="verificationCode" className="block text-sm font-medium text-white">
                  Verification code
                </label>
                <div className="mt-1">
                  <input
                    id="verificationCode"
                    name="verificationCode"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    required
                    maxLength={6}
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="appearance-none relative block w-full px-3 py-2 border border-white/30 placeholder-gray-400 text-gray-900 rounded-md focus:outline-none focus:ring-struvital-primary focus:border-struvital-primary focus:z-10 sm:text-sm bg-white/90 text-center tracking-widest text-lg"
                    placeholder="000000"
                    autoFocus
                  />
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isLoading || verificationCode.length < 6}
                  className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-struvital-primary hover:bg-struvital-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-struvital-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Verifying...
                    </>
                  ) : (
                    'Verify'
                  )}
                </button>
              </div>

              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={handleBackToLogin}
                  className="text-sm text-white/80 hover:text-white underline transition-colors"
                >
                  Back to login
                </button>
                {needsCodeSend && (
                  <button
                    type="button"
                    onClick={handleResendCode}
                    className="text-sm text-white/80 hover:text-white underline transition-colors"
                  >
                    Resend code
                  </button>
                )}
              </div>
            </form>
          )}
        </div>

        <div className="text-center">
          <p className="text-sm text-white/80">
            This dashboard connects to ThingsBoard for authentication.
            <br />
            Contact your administrator if you need access.
          </p>
        </div>
      </div>
    </div>
  );
}
